import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useChildProfile } from "./ChildProfileContext";

const SettingsContext = createContext(null);

function resolveStreakVisibility(mode, difficulty) {
  if (mode === "always") return true;
  if (mode === "never") return false;
  // "auto" → hide in easy, show in medium/hard
  return difficulty !== "easy";
}

export function SettingsProvider({ children }) {
  const { selected } = useChildProfile();
  const [override, setOverride] = useState({}); // in-session toggles

  const settings = useMemo(() => {
    const base = selected?.settings || {};
    const acc = selected?.accessibilitySettings || {};
    return {
      music: override.music ?? base.music ?? "on",
      soundEffects: override.soundEffects ?? base.soundEffects ?? "on",
      reducedMotion: override.reducedMotion ?? Boolean(base.reducedMotion),
      highContrast: override.highContrast ?? Boolean(base.highContrast),
      narrationSpeed: override.narrationSpeed ?? selected?.narrationSpeed ?? "slow",
      difficulty: override.difficulty ?? selected?.difficulty ?? "easy",
      showStreakCounterMode:
        override.showStreakCounterMode ?? acc.showStreakCounter ?? "auto",
    };
  }, [selected, override]);

  const showStreakCounter = resolveStreakVisibility(
    settings.showStreakCounterMode,
    settings.difficulty,
  );

  useEffect(() => {
    const html = document.documentElement;
    html.classList.toggle("high-contrast", settings.highContrast);
    html.classList.toggle("reduced-motion", settings.reducedMotion);
  }, [settings.highContrast, settings.reducedMotion]);

  const value = {
    ...settings,
    showStreakCounter,
    setOverride: (patch) => setOverride((prev) => ({ ...prev, ...patch })),
  };

  return <SettingsContext.Provider value={value}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error("useSettings must be used within SettingsProvider");
  return ctx;
}
